import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote, PenLine, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { ReviewForm } from '@/components/reviews/ReviewForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TestimonialsSection = () => {
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fallback testimonials (shown when no approved reviews exist)
  const fallbackTestimonials = [];

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews?approved_only=true`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const testimonials = reviews.length > 0 ? reviews.map(review => ({
    id: review.id,
    name: review.name,
    company: review.company || '',
    role: review.project_type || '',
    content: review.content,
    title: review.title,
    rating: review.rating,
    image: `https://ui-avatars.com/api/?name=${encodeURIComponent(review.name)}&background=0040c1&color=fff&size=150`
  })) : fallbackTestimonials;

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    if (testimonials.length === 0) return;
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) nextIndex = testimonials.length - 1;
      if (nextIndex >= testimonials.length) nextIndex = 0;
      return nextIndex;
    });
  };

  // Auto-advance
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(() => {
      paginate(1);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const currentTestimonial = testimonials[currentIndex];

  const writeReviewText = {
    nl: 'Schrijf een review',
    en: 'Write a review',
    fr: 'Écrire un avis',
    de: 'Bewertung schreiben'
  };

  const noReviewsText = {
    nl: 'Nog geen reviews. Wees de eerste!',
    en: 'No reviews yet. Be the first!',
    fr: 'Pas encore d\'avis. Soyez le premier!',
    de: 'Noch keine Bewertungen. Seien Sie der Erste!'
  };

  // If no reviews and not loading, show call-to-action
  if (!loading && testimonials.length === 0) {
    return (
      <section className="section-padding bg-white overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star size={16} className="fill-current" />
              <span>{t('testimonials.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] mb-6">
              {t('testimonials.title')} <span className="text-gradient">{t('testimonials.titleHighlight')}</span>
            </h2>
            <p className="text-lg text-[#475569] mb-8">
              {noReviewsText[language] || noReviewsText.nl}
            </p>
            <button
              onClick={() => setShowReviewForm(true)}
              className="btn-accent inline-flex items-center gap-2"
            >
              <PenLine size={18} />
              {writeReviewText[language] || writeReviewText.nl}
            </button>
          </motion.div>
        </div>

        {/* Review Form Modal */}
        <AnimatePresence>
          {showReviewForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowReviewForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-[#0f172a]">
                    {writeReviewText[language] || writeReviewText.nl}
                  </h3>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    className="p-2 hover:bg-slate-100 rounded-full"
                  >
                    <X size={20} />
                  </button>
                </div>
                <ReviewForm 
                  onClose={() => setShowReviewForm(false)}
                  onSuccess={() => {
                    fetchReviews();
                  }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    );
  }

  return (
    <section className="section-padding bg-white overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Star size={16} className="fill-current" />
            <span>{t('testimonials.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] mb-6">
            {t('testimonials.title')} <span className="text-gradient">{t('testimonials.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-[#475569]">
            {t('testimonials.description')}
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        {currentTestimonial && (
          <div className="relative max-w-4xl mx-auto">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = swipePower(offset.x, velocity.x);
                  if (swipe < -swipeConfidenceThreshold) {
                    paginate(1);
                  } else if (swipe > swipeConfidenceThreshold) {
                    paginate(-1);
                  }
                }}
                className="testimonial-card bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-100"
                data-testid={`testimonial-${currentIndex}`}
              >
                <Quote className="w-12 h-12 text-[#2563EB]/20 mb-6" />
                
                {currentTestimonial.title && (
                  <h3 className="text-xl font-bold text-[#0f172a] mb-4">
                    "{currentTestimonial.title}"
                  </h3>
                )}
                
                <p className="text-lg md:text-xl text-[#475569] leading-relaxed mb-8">
                  {currentTestimonial.content}
                </p>

                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={currentTestimonial.image}
                      alt={currentTestimonial.name}
                      loading="lazy"
                      decoding="async"
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#2563EB]/20"
                    />
                    <div>
                      <p className="font-bold text-[#0f172a]">{currentTestimonial.name}</p>
                      <p className="text-[#475569] text-sm">
                        {currentTestimonial.role && `${currentTestimonial.role}`}
                        {currentTestimonial.role && currentTestimonial.company && ' • '}
                        {currentTestimonial.company}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(currentTestimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[#f59e0b] fill-current" />
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-center items-center gap-4 mt-8">
              <button
                onClick={() => paginate(-1)}
                className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-all"
                aria-label="Previous review"
                data-testid="testimonial-prev"
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setDirection(index > currentIndex ? 1 : -1);
                      setCurrentIndex(index);
                    }}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'bg-[#2563EB] w-8' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    aria-label={`Go to review ${index + 1}`}
                    data-testid={`testimonial-dot-${index}`}
                  />
                ))}
              </div>

              <button
                onClick={() => paginate(1)}
                className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-all"
                aria-label="Next review"
                data-testid="testimonial-next"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Write Review Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <button
            onClick={() => setShowReviewForm(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-[#2563EB] text-[#2563EB] rounded-xl font-semibold hover:bg-[#2563EB] hover:text-white transition-all"
          >
            <PenLine size={18} />
            {writeReviewText[language] || writeReviewText.nl}
          </button>
        </motion.div>
      </div>

      {/* Review Form Modal */}
      <AnimatePresence>
        {showReviewForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowReviewForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#0f172a]">
                  {writeReviewText[language] || writeReviewText.nl}
                </h3>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
              <ReviewForm 
                onClose={() => setShowReviewForm(false)}
                onSuccess={() => {
                  fetchReviews();
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
